import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Zap, TrendingUp, Target, Mail, RefreshCw, Loader2,
  CheckCircle2, PauseCircle, Send, Eye, UserPlus,
  BarChart3, ArrowUpRight, Play, ChevronRight, MessageSquare, Reply,
  FlaskConical, Activity, Clock, Trophy,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import api from '../../lib/api';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: Play },
  completed: { color: 'bg-blue-500/10 text-blue-500 border-blue-500/20', icon: CheckCircle2 },
  paused: { color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: PauseCircle },
};

export default function OutreachDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [markingReply, setMarkingReply] = useState(null);
  const [abData, setAbData] = useState(null);
  const [workerStatus, setWorkerStatus] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, abRes, workerRes] = await Promise.allSettled([
        api.get(`/api/brain/cadence-analytics/outreach-dashboard?days=${days}`),
        api.get('/api/brain/cadence-analytics/ab-test-results'),
        api.get('/api/brain/cadence-analytics/worker-status'),
      ]);
      if (dashRes.status === 'fulfilled') setData(dashRes.value.data);
      if (abRes.status === 'fulfilled') setAbData(abRes.value.data);
      if (workerRes.status === 'fulfilled') setWorkerStatus(workerRes.value.data);
    } catch {
      toast.error('Failed to load outreach data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMarkReply = async (leadId, leadName) => {
    setMarkingReply(leadId);
    try {
      const res = await api.post('/api/brain/cadence-analytics/mark-reply', { lead_id: leadId });
      toast.success(res.data.message || `Reply recorded for ${leadName}`);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark reply');
    } finally {
      setMarkingReply(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-1" data-testid="outreach-loading">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-7 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" />
          </div>
          <div className="h-9 w-24 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-4 rounded-lg border space-y-2">
              <div className="h-4 w-20 bg-muted/60 rounded animate-pulse" />
              <div className="h-7 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-lg border overflow-hidden">
          <div className="flex gap-4 p-4 border-b bg-muted/30">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 flex-1 bg-muted rounded animate-pulse" />
            ))}
          </div>
          {[...Array(5)].map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 p-4 border-b">
              {[...Array(5)].map((_, colIndex) => (
                <div key={colIndex} className="h-4 flex-1 bg-muted/60 rounded animate-pulse" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const ov = data.overview;

  return (
    <div className="space-y-6 p-1" data-testid="outreach-dashboard">
      {/* Header + Worker Status */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="outreach-title">
            Outreach Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Autonomous lead discovery, qualification & email outreach
          </p>
        </div>
        <div className="flex items-center gap-2">
          {workerStatus && <WorkerBadge status={workerStatus} />}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border rounded-md px-2 py-1.5 bg-background"
            data-testid="outreach-period-select"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="outreach-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard title="Auto-Revealed" value={ov.total_auto_revealed} sub={`${ov.recent_auto_revealed} this period`} icon={UserPlus} color="text-blue-500" testId="kpi-auto-revealed" />
        <KpiCard title="Active Cadences" value={ov.active_cadences} sub={`${ov.total_cadences} total`} icon={Zap} color="text-emerald-500" testId="kpi-active-cadences" />
        <KpiCard title="Emails Sent" value={ov.emails_sent} sub={`${ov.emails_drafted} drafted`} icon={Send} color="text-violet-500" testId="kpi-emails-sent" />
        <KpiCard title="Open Rate" value={`${ov.open_rate}%`} sub={`${ov.emails_opened} opened`} icon={Eye} color="text-teal-500" testId="kpi-open-rate" />
        <KpiCard title="Replies" value={ov.leads_replied || 0} sub={`${ov.reply_rate || 0}% reply rate`} icon={MessageSquare} color="text-orange-500" testId="kpi-replies" highlight={ov.leads_replied > 0} />
        <KpiCard title="Steps Done" value={ov.steps_executed} sub={`of ${ov.steps_total} total`} icon={Target} color="text-amber-500" testId="kpi-steps" />
        <KpiCard title="Converted" value={ov.pipeline_converted} sub="moved past new" icon={TrendingUp} color="text-rose-500" testId="kpi-converted" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="daily-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-500" /> Daily Auto-Reveals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.daily_trend}>
                  <defs>
                    <linearGradient id="revealFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(204, 94%, 40%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(204, 94%, 40%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 9%, 85%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="hsl(204, 94%, 40%)" fill="url(#revealFill)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="No auto-reveals yet. Run a lead discovery to get started." />
            )}
          </CardContent>
        </Card>

        <Card data-testid="cadence-status-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-emerald-500" /> Cadence Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ov.total_cadences > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { name: 'Active', value: ov.active_cadences, fill: '#10b981' },
                  { name: 'Completed', value: ov.completed_cadences, fill: '#3b82f6' },
                  { name: 'Paused', value: ov.paused_cadences, fill: '#f59e0b' },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 9%, 85%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="No outreach cadences yet." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* A/B Testing Section */}
      {abData && <ABTestSection data={abData} />}

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Auto-Revealed Leads */}
        <Card data-testid="auto-revealed-leads-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-500" /> Recent Auto-Revealed Leads
              {(ov.leads_replied || 0) > 0 && (
                <Badge variant="outline" className="text-[10px] bg-orange-500/10 text-orange-500 border-orange-500/20 ml-auto">
                  {ov.leads_replied} replied
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b text-muted-foreground">
                  <th className="text-left p-2 font-medium">Name</th>
                  <th className="text-left p-2 font-medium">Title</th>
                  <th className="text-center p-2 font-medium">Score</th>
                  <th className="text-center p-2 font-medium">Status</th>
                  <th className="text-center p-2 font-medium">Action</th>
                </tr></thead>
                <tbody>
                  {(data.auto_revealed_leads || []).slice(0, 10).map((lead, i) => (
                    <tr key={i} className={`border-b last:border-0 hover:bg-muted/40 ${lead.replied ? 'bg-orange-500/5' : ''}`} data-testid={`lead-row-${i}`}>
                      <td className="p-2 font-medium">
                        <div className="flex items-center gap-1.5">
                          {lead.name}
                          {lead.replied && (
                            <Badge variant="outline" className="text-[9px] bg-orange-500/10 text-orange-500 border-orange-500/20 px-1" data-testid={`reply-badge-${i}`}>replied</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-muted-foreground truncate max-w-[100px]">{lead.title || '-'}</td>
                      <td className="p-2 text-center"><Badge variant="outline" className="text-[10px]">{lead.icp_score}%</Badge></td>
                      <td className="p-2 text-center"><Badge variant="secondary" className="text-[10px] capitalize">{lead.status}</Badge></td>
                      <td className="p-2 text-center">
                        {lead.replied ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-orange-500 mx-auto" />
                        ) : (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={() => handleMarkReply(lead.id, lead.name)} disabled={markingReply === lead.id} data-testid={`mark-reply-btn-${i}`}>
                            {markingReply === lead.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Reply className="h-3 w-3 mr-0.5" /> Mark Reply</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!data.auto_revealed_leads?.length) && (
                    <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No auto-revealed leads yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Active Cadences */}
        <Card data-testid="cadences-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-emerald-500" /> Outreach Cadences
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b text-muted-foreground">
                  <th className="text-left p-2 font-medium">Lead</th>
                  <th className="text-left p-2 font-medium">Cadence</th>
                  <th className="text-center p-2 font-medium">Progress</th>
                  <th className="text-center p-2 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {(data.cadences || []).slice(0, 8).map((c, i) => {
                    const pct = c.total_steps > 0 ? Math.round((c.current_step / c.total_steps) * 100) : 0;
                    const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.active;
                    return (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/40" data-testid={`cadence-row-${i}`}>
                        <td className="p-2 font-medium">{c.lead_name}</td>
                        <td className="p-2 text-muted-foreground truncate max-w-[140px]">{c.cadence_name}</td>
                        <td className="p-2">
                          <div className="flex items-center gap-1.5">
                            <Progress value={pct} className="h-1.5 flex-1" />
                            <span className="text-[10px] text-muted-foreground w-8">{c.current_step}/{c.total_steps}</span>
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          <Badge variant="outline" className={`text-[10px] capitalize ${cfg.color}`}>{c.status}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                  {(!data.cadences?.length) && (
                    <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No outreach cadences yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Emails */}
      <Card data-testid="recent-emails-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-violet-500" /> Recent Outreach Emails
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b text-muted-foreground">
                <th className="text-left p-2 font-medium">Subject</th>
                <th className="text-center p-2 font-medium">Status</th>
                <th className="text-center p-2 font-medium">Channel</th>
                <th className="text-center p-2 font-medium">Opened</th>
                <th className="text-center p-2 font-medium">Replied</th>
                <th className="text-left p-2 font-medium">Date</th>
              </tr></thead>
              <tbody>
                {(data.recent_emails || []).slice(0, 10).map((e, i) => (
                  <tr key={i} className={`border-b last:border-0 hover:bg-muted/40 ${e.replied ? 'bg-orange-500/5' : ''}`} data-testid={`email-row-${i}`}>
                    <td className="p-2 font-medium truncate max-w-[220px]">{e.subject || '(no subject)'}</td>
                    <td className="p-2 text-center">
                      <Badge variant={e.status === 'sent' ? 'default' : 'secondary'} className="text-[10px] capitalize">{e.status}</Badge>
                    </td>
                    <td className="p-2 text-center text-muted-foreground">{e.sent_mode || '-'}</td>
                    <td className="p-2 text-center">
                      {e.opened ? <Eye className="h-3.5 w-3.5 text-teal-500 mx-auto" /> : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="p-2 text-center" data-testid={`email-reply-${i}`}>
                      {e.replied ? <MessageSquare className="h-3.5 w-3.5 text-orange-500 mx-auto" /> : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="p-2 text-muted-foreground">{formatDate(e.created_at)}</td>
                  </tr>
                ))}
                {(!data.recent_emails?.length) && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No outreach emails yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Funnel */}
      <Card data-testid="pipeline-funnel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ArrowUpRight className="h-4 w-4 text-rose-500" /> Autonomous Pipeline Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 overflow-x-auto py-2">
            <FunnelStep label="Discovered" value={ov.total_auto_revealed} color="bg-blue-500" />
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <FunnelStep label="Enrolled" value={ov.total_cadences} color="bg-emerald-500" />
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <FunnelStep label="Emails Sent" value={ov.emails_sent} color="bg-violet-500" />
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <FunnelStep label="Opened" value={ov.emails_opened} color="bg-teal-500" />
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <FunnelStep label="Replied" value={ov.leads_replied || 0} color="bg-orange-500" />
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <FunnelStep label="Converted" value={ov.pipeline_converted} color="bg-rose-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── A/B Testing Section ── */
function ABTestSection({ data }) {
  if (!data || data.total_tests === 0) {
    return (
      <Card data-testid="ab-test-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-indigo-500" /> A/B Subject Testing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground text-center py-6">
            No A/B tests yet. Subject variants are auto-generated when cadence emails are created.
          </div>
        </CardContent>
      </Card>
    );
  }

  const a = data.variant_a;
  const b = data.variant_b;
  const winner = data.winner;

  return (
    <Card data-testid="ab-test-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-indigo-500" /> A/B Subject Testing
          <Badge variant="outline" className="text-[10px] ml-auto">{data.total_tests} tests</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Variant A */}
          <div className={`rounded-lg border p-3 ${winner === 'a' ? 'ring-1 ring-emerald-500/40 bg-emerald-500/5' : ''}`} data-testid="variant-a-card">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="text-[10px] bg-blue-500/10 text-blue-500 border-blue-500/20">Variant A</Badge>
              {winner === 'a' && <Trophy className="h-3.5 w-3.5 text-emerald-500" />}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-muted-foreground">Sent</span><p className="font-bold">{a.sent}</p></div>
              <div><span className="text-muted-foreground">Opened</span><p className="font-bold">{a.opened}</p></div>
              <div><span className="text-muted-foreground">Open Rate</span><p className="font-bold">{a.open_rate}%</p></div>
            </div>
            {a.sample_subjects?.length > 0 && (
              <div className="mt-2 text-[10px] text-muted-foreground truncate">"{a.sample_subjects[0]}"</div>
            )}
          </div>

          {/* Variant B */}
          <div className={`rounded-lg border p-3 ${winner === 'b' ? 'ring-1 ring-emerald-500/40 bg-emerald-500/5' : ''}`} data-testid="variant-b-card">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="text-[10px] bg-violet-500/10 text-violet-500 border-violet-500/20">Variant B</Badge>
              {winner === 'b' && <Trophy className="h-3.5 w-3.5 text-emerald-500" />}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-muted-foreground">Sent</span><p className="font-bold">{b.sent}</p></div>
              <div><span className="text-muted-foreground">Opened</span><p className="font-bold">{b.opened}</p></div>
              <div><span className="text-muted-foreground">Open Rate</span><p className="font-bold">{b.open_rate}%</p></div>
            </div>
            {b.sample_subjects?.length > 0 && (
              <div className="mt-2 text-[10px] text-muted-foreground truncate">"{b.sample_subjects[0]}"</div>
            )}
          </div>
        </div>

        {/* Recent Tests Table */}
        {data.recent_tests?.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b text-muted-foreground">
                <th className="text-left p-1.5 font-medium">Variant A</th>
                <th className="text-left p-1.5 font-medium">Variant B</th>
                <th className="text-center p-1.5 font-medium">Selected</th>
                <th className="text-center p-1.5 font-medium">Opened</th>
              </tr></thead>
              <tbody>
                {data.recent_tests.slice(0, 5).map((t, i) => (
                  <tr key={i} className="border-b last:border-0" data-testid={`ab-row-${i}`}>
                    <td className={`p-1.5 truncate max-w-[160px] ${t.selected === 'a' ? 'font-medium' : 'text-muted-foreground'}`}>{t.variant_a}</td>
                    <td className={`p-1.5 truncate max-w-[160px] ${t.selected === 'b' ? 'font-medium' : 'text-muted-foreground'}`}>{t.variant_b}</td>
                    <td className="p-1.5 text-center">
                      <Badge variant="outline" className="text-[9px]">{t.selected.toUpperCase()}</Badge>
                    </td>
                    <td className="p-1.5 text-center">
                      {t.opened ? <Eye className="h-3 w-3 text-teal-500 mx-auto" /> : <span className="text-muted-foreground">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Worker Status Badge ── */
function WorkerBadge({ status }) {
  const last = status.last_run;
  const nextRun = status.next_run_at;
  const tenant = status.tenant || {};

  let timeAgo = '';
  if (last?.completed_at) {
    const diff = Math.round((Date.now() - new Date(last.completed_at).getTime()) / 60000);
    timeAgo = diff < 1 ? 'just now' : diff < 60 ? `${diff}m ago` : `${Math.round(diff / 60)}h ago`;
  }

  let nextIn = '';
  if (nextRun) {
    const diff = Math.round((new Date(nextRun).getTime() - Date.now()) / 60000);
    nextIn = diff <= 0 ? 'due now' : diff < 60 ? `in ${diff}m` : `in ${Math.round(diff / 60)}h`;
  }

  // Phase 3 — show THIS tenant's executed steps (not the global worker total).
  const myExecuted = tenant.last_executed || 0;

  return (
    <div className="flex items-center gap-1.5 text-[10px] border rounded-md px-2 py-1" data-testid="worker-status-badge">
      <Activity className="h-3 w-3 text-emerald-500" />
      <span className="text-muted-foreground">Worker:</span>
      {last ? (
        <>
          <span className="font-medium">{timeAgo}</span>
          {myExecuted > 0 && <span className="text-muted-foreground" data-testid="worker-tenant-executed">({myExecuted} sent)</span>}
          {nextIn && <><span className="text-muted-foreground">|</span><Clock className="h-2.5 w-2.5 text-muted-foreground" /><span>{nextIn}</span></>}
        </>
      ) : (
        <span className="text-muted-foreground">awaiting first run</span>
      )}
    </div>
  );
}

/* ── Shared Components ── */
function KpiCard({ title, value, sub, icon: Icon, color, testId, highlight }) {
  return (
    <Card className={`relative overflow-hidden ${highlight ? 'ring-1 ring-orange-500/30' : ''}`} data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-muted-foreground font-medium">{title}</p>
            <p className="text-xl font-bold mt-0.5">{value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>
          </div>
          <Icon className={`h-5 w-5 ${color} opacity-70`} />
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({ label, value, color }) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-[70px]" data-testid={`funnel-${label.toLowerCase()}`}>
      <div className={`${color} text-white text-sm font-bold rounded-full h-10 w-10 flex items-center justify-center`}>{value}</div>
      <span className="text-[10px] text-muted-foreground font-medium">{label}</span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">{text}</div>;
}

function formatDate(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return '-'; }
}
